import { createFileRoute } from '@tanstack/react-router'
import { ComparisonPage, comparisonScripts, type ComparisonData } from '@/components/comparison-page'
import { MONITOR_LOCATIONS } from '@/lib/check-locations'

export const Route = createFileRoute('/_site/compare/uptrends')({
  component: () => <ComparisonPage data={data} />,
  head: () => ({
    meta: [
      { title: 'Uptrends Alternative — Uptrack Uptime Monitoring' },
      { name: 'description', content: "Uptrack vs Uptrends: Uptrends starts at $42/mo with 2-min checks; 30s checks are on Enterprise. Uptrack checks every 30s from $3/mo." },
      { property: 'og:title', content: 'Uptrends Alternative — Uptrack' },
      { property: 'og:description', content: "Compare Uptrack and Uptrends on price, check intervals, checkpoints, status pages, and real user monitoring." },
      { property: 'og:image', content: 'https://uptrack.app/og/compare-uptrends.png' },
      { name: 'twitter:card', content: 'summary_large_image' },
    ],
    links: [
      { rel: 'canonical', href: 'https://uptrack.app/compare/uptrends' },
    ],
    scripts: comparisonScripts('uptrends', "Uptrends"),
  }),
})

const data: ComparisonData = {
  competitor: "Uptrends",
  tagline: "Uptrends is a monitoring platform with 230+ checkpoints, real user monitoring, and multi-step API checks, with plans from $42/mo. Uptrack checks every 30 seconds from $3/mo.",
  heroDescription: "These facts come from Uptrends's public pricing and docs pages as of October 2026. Plans change often, so check their site before you decide.",

  features: [
    { feature: "Starting price", uptrack: "$3/mo (Pro)", competitor: "From $42/mo billed annually (Core)", uptrackWins: true },
    { feature: "Check interval (entry plan)", uptrack: "30 sec", competitor: "2 min (Core)", uptrackWins: true },
    { feature: "30-second checks", uptrack: "Every plan", competitor: "Enterprise plan", uptrackWins: true },
    { feature: "Checkpoints", uptrack: MONITOR_LOCATIONS.compare.cell, competitor: "US and EU on Core; 230+ from Pro" },
    { feature: "Status pages", uptrack: "5 on Pro, unlimited on Team", competitor: "10 on Core, unlimited from Pro" },
    { feature: "Users (entry plan)", uptrack: "3 on Pro", competitor: "10 on Core" },
    { feature: "Real User Monitoring", uptrack: "Not available", competitor: "Available" },
    { feature: "Multi-step API checks", uptrack: "Not available", competitor: "Included" },
  ],

  painPoints: [
    {
      title: "Entry price",
      description: "Uptrends Core starts at $42/mo billed annually ($46 monthly). Uptrack Pro is $3/mo for 50 monitors.",
    },
    {
      title: "30-second checks are on Enterprise",
      description: "Uptrends Core checks every 2 minutes and Pro every minute. 30-second checks need the Enterprise plan. Every Uptrack plan checks every 30 seconds.",
    },
    {
      title: "Checkpoints by plan",
      description: "Uptrends Core uses US and EU checkpoints. The 230+ checkpoint network starts on Pro, from $60/mo billed annually.",
    },
  ],

  differentiators: [
    "Pro starts at $3/mo for 50 monitors at 30-second checks.",
    "30-second checks on every plan.",
    "Alert confirmation: Uptrack opens an incident only after 2-5 consecutive failed checks, and several check locations must confirm. One DNS timeout or CDN blip does not page you.",
    "Browser checks: Pro includes 5 Playwright monitors that run flows like login or checkout and save a screenshot at each step.",
    "REST API and an MCP server, so you can manage monitors from scripts or AI assistants such as Claude.",
  ],

  notForYou: [
    "If you need 230+ monitoring locations worldwide, Uptrends has one of the largest checkpoint networks.",
    "If you need Real User Monitoring (RUM) to measure performance from actual user browsers.",
    "If you need multi-step API monitoring to test complex API workflows.",
    "If you're an enterprise that needs SLA reporting, custom dashboards, and dedicated account management.",
    "If geographic coverage is critical and you need to check from specific regions or cities.",
  ],
}
